import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Button, ShimmerDiv } from 'fluxo-ui';
import { BarChartIcon, CalendarIcon, CheckCircleIcon, ClockIcon, FlagIcon, PlayIcon } from 'fluxo-ui/icons';

import { Badge, Card, EmptyState, PageHeader, StatCard } from '../common';
import { DonutChart, LineChart } from '../charts';
import analyticsService from '../../services/analytics.service';
import attemptService from '../../services/attempt.service';
import examService from '../../services/exam.service';
import { useAuthStore } from '../../store/authStore';

import type { BadgeTone } from '../common/Badge';

interface StudentStats {
    availableExams: number;
    completedExams: number;
    avgScore: number;
    totalTimeSpent: number;
    inProgressExams: number;
    certificates: number;
}

interface InProgressExam {
    id: string;
    attemptId: string;
    title: string;
    attemptNumber: number;
    startedAt?: string;
}

interface AvailableExam {
    id: string;
    title: string;
    type: string;
    duration: string;
    questions: number;
    attempted: boolean;
}

const examTypeTones: Record<string, BadgeTone> = {
    certification: 'violet',
    assessment: 'accent',
    practice: 'success',
    survey: 'info',
    live_quiz: 'warning',
};

const emptyStats: StudentStats = {
    availableExams: 0,
    completedExams: 0,
    avgScore: 0,
    totalTimeSpent: 0,
    inProgressExams: 0,
    certificates: 0,
};

const StudentDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [stats, setStats] = useState<StudentStats | null>(null);
    const [availableExams, setAvailableExams] = useState<AvailableExam[]>([]);
    const [inProgressExams, setInProgressExams] = useState<InProgressExam[]>([]);
    const [passFail, setPassFail] = useState<{ passed: number; failed: number }>({ passed: 0, failed: 0 });
    const [scoreTrend, setScoreTrend] = useState<{ label: string; value: number }[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const [data, examsResponse, attempts] = await Promise.all([
                    analyticsService.getStudentStats().catch(() => null),
                    examService.listExams({ status: 'active', limit: 6 }).catch(() => ({ exams: [] }) as any),
                    attemptService.getUserAttempts().catch(() => []),
                ]);

                setStats(data || emptyStats);

                const activeExams = examsResponse?.exams || [];
                const examById = new Map(activeExams.map((e: any) => [String(e._id), e]));

                setInProgressExams(
                    (attempts || [])
                        .filter((a: any) => a.status === 'in_progress')
                        .slice(0, 4)
                        .map((a: any) => {
                            const examId = String((a.examId as any)?._id || a.examId);
                            const exam: any = (a.examId as any)?.title ? a.examId : examById.get(examId);
                            return {
                                id: examId,
                                attemptId: a._id,
                                title: exam?.title || 'Exam in progress',
                                attemptNumber: a.attemptNumber || 1,
                                startedAt: a.startTime,
                            };
                        }),
                );

                const completedAttempts = (attempts || []).filter((a: any) => a.status === 'submitted' || a.status === 'auto_submitted');
                setPassFail({
                    passed: completedAttempts.filter((a: any) => a.passed).length,
                    failed: completedAttempts.filter((a: any) => !a.passed).length,
                });

                const attemptedExamIds = new Set((attempts || []).map((a: any) => String((a.examId as any)?._id || a.examId)));
                setAvailableExams(
                    activeExams.map((e: any) => ({
                        id: e._id,
                        title: e.title,
                        type: e.type,
                        duration: e.duration ? `${Math.round(e.duration / 60)} min` : '—',
                        questions: e.questions?.length || 0,
                        attempted: attemptedExamIds.has(String(e._id)),
                    })),
                );
            } catch (error) {
                console.error('Failed to fetch student dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    useEffect(() => {
        if (!user?._id) return;
        analyticsService
            .getUserAnalytics(user._id)
            .then((data: any) => {
                const history: any[] = Array.isArray(data?.attemptHistory) ? data.attemptHistory : [];
                setScoreTrend(
                    history
                        .filter((a) => a.date && !Number.isNaN(Number(a.score)))
                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                        .slice(-20)
                        .map((a) => ({
                            label: new Date(a.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
                            value: Math.round(Number(a.score)),
                        })),
                );
            })
            .catch(() => setScoreTrend([]));
    }, [user?._id]);

    const hasPerformanceData = scoreTrend.length > 0 || passFail.passed + passFail.failed > 0;
    const userName = user?.profile?.name || user?.name || user?.email || '';

    if (loading) {
        return (
            <div className="space-y-8">
                <ShimmerDiv level={2} className="h-20 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, i) => (
                        <ShimmerDiv key={i} level={3} className="h-32" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title={userName ? `Welcome back, ${userName}!` : 'Welcome back!'}
                description="Continue your learning journey"
                className="mb-0"
            />

            {stats && stats.inProgressExams > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl border bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800">
                    <div className="flex items-center gap-3">
                        <FlagIcon className="w-5 h-5 text-teal-600 dark:text-teal-400 shrink-0" />
                        <span className="text-sm font-medium text-slate-900 dark:text-white">
                            You have {stats.inProgressExams} {stats.inProgressExams === 1 ? 'exam' : 'exams'} in progress
                        </span>
                    </div>
                    <Button
                        label="Continue"
                        variant="primary"
                        size="sm"
                        onClick={() => inProgressExams[0] && navigate(`/take-exam/${inProgressExams[0].id}`)}
                    />
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                <StatCard
                    label="Available Exams"
                    value={stats?.availableExams || availableExams.length}
                    icon={CalendarIcon}
                    tone="blue"
                    onClick={() => navigate('/exams')}
                />
                <StatCard
                    label="Completed"
                    value={stats?.completedExams ?? 0}
                    icon={CheckCircleIcon}
                    tone="emerald"
                    hint={`${stats?.inProgressExams || inProgressExams.length} in progress`}
                    onClick={() => navigate('/exams')}
                />
                <StatCard
                    label="Average Score"
                    value={`${stats?.avgScore ?? 0}%`}
                    icon={BarChartIcon}
                    tone="violet"
                    onClick={() => navigate('/analytics')}
                />
                <StatCard
                    label="Time Spent"
                    value={`${stats?.totalTimeSpent ?? 0}h`}
                    icon={ClockIcon}
                    tone="amber"
                    hint={`${stats?.certificates ?? 0} certificates`}
                    onClick={() => navigate('/analytics')}
                />
            </div>

            <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Your Performance</h2>
                {hasPerformanceData ? (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                        <Card
                            className="lg:col-span-2"
                            title="Score Trend"
                            actions={
                                <span className="text-xs text-slate-400 dark:text-slate-500">Last {scoreTrend.length} attempts</span>
                            }
                        >
                            {scoreTrend.length > 1 ? (
                                <LineChart data={scoreTrend} height={190} formatValue={(v) => `${v}%`} />
                            ) : (
                                <div className="text-sm text-slate-400 dark:text-slate-500 text-center py-10">
                                    Complete more exams to see your score trend
                                </div>
                            )}
                        </Card>
                        <Card title="Pass Rate">
                            <DonutChart
                                data={[
                                    { label: 'Passed', value: passFail.passed, colorIndex: 1 },
                                    { label: 'Failed', value: passFail.failed, colorIndex: 4 },
                                ]}
                                size={140}
                                centerSub="Completed"
                            />
                        </Card>
                    </div>
                ) : (
                    <Card>
                        <EmptyState
                            icon={BarChartIcon}
                            title="No performance data yet"
                            description="Take your first exam to start tracking score trends"
                            action={<Button label="Browse Exams" variant="primary" onClick={() => navigate('/exams')} />}
                        />
                    </Card>
                )}
            </div>

            {inProgressExams.length > 0 && (
                <div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Continue Where You Left Off</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {inProgressExams.map((exam) => (
                            <Card key={exam.attemptId} interactive>
                                <h3 className="font-semibold text-slate-900 dark:text-white mb-3">{exam.title}</h3>
                                <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                                    <div className="flex items-center justify-between">
                                        <span>Attempt</span>
                                        <span className="font-medium text-slate-900 dark:text-white tabular-nums">
                                            #{exam.attemptNumber}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span>Started</span>
                                        <span className="font-medium text-slate-900 dark:text-white">
                                            {exam.startedAt ? new Date(exam.startedAt).toLocaleString() : '—'}
                                        </span>
                                    </div>
                                </div>
                                <Button
                                    label="Resume Exam"
                                    variant="primary"
                                    className="w-full mt-4"
                                    leftIcon={PlayIcon}
                                    onClick={() => navigate(`/take-exam/${exam.id}`)}
                                />
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            <div>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Available Exams</h2>
                    <Button label="Browse All" variant="default" size="sm" layout="outlined" onClick={() => navigate('/exams')} />
                </div>
                {availableExams.length === 0 ? (
                    <Card>
                        <EmptyState
                            icon={CalendarIcon}
                            title="No active exams right now"
                            description="Check back later or browse all exams"
                            action={<Button label="Browse All Exams" variant="primary" onClick={() => navigate('/exams')} />}
                        />
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {availableExams.map((exam) => (
                            <Card key={exam.id} interactive>
                                <div className="flex items-start justify-between mb-3 gap-2">
                                    <h3 className="font-semibold text-slate-900 dark:text-white">{exam.title}</h3>
                                    <Badge tone={examTypeTones[exam.type] || 'neutral'}>{exam.type?.replace(/_/g, ' ')}</Badge>
                                </div>
                                <div className="space-y-2 text-sm mb-4">
                                    <div className="flex items-center justify-between text-slate-600 dark:text-slate-400">
                                        <span>Duration</span>
                                        <span className="font-medium text-slate-900 dark:text-white">{exam.duration}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-slate-600 dark:text-slate-400">
                                        <span>Questions</span>
                                        <span className="font-medium text-slate-900 dark:text-white tabular-nums">{exam.questions}</span>
                                    </div>
                                </div>
                                <Button
                                    label={exam.attempted ? 'Take Again' : 'Start Exam'}
                                    variant="primary"
                                    className="w-full"
                                    onClick={() => navigate(`/take-exam/${exam.id}`)}
                                />
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StudentDashboard;
